/**
 * Модуль по умолчанию для строки создания на главной (ТЗ главной, § 9): последний модуль, куда
 * ученик добавлял карточки, — в том числе копия готового и стартовый модуль знакомства.
 * Хранение — localStorage на пользователя, до трёх недавних, свежий первым; модуль хранится
 * вместе с названием, чтобы чип не мигал, пока список модулей перечитывается.
 * Без alias-импортов, хранилище передаётся аргументом — модуль проверяют тесты.
 */

/** Хранилище «ключ — строка»: localStorage или подмена в тестах. */
export interface RecentModulesStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface RecentModule {
  id: string;
  title: string;
}

export const RECENT_MODULES_LIMIT = 3;

export const recentModulesKey = (userId: string) => `lx.composer.modules.${userId}`;

function browserStore(): RecentModulesStore | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Недавние модули пользователя; битая запись или недоступное хранилище — пусто. */
export function readRecentModules(
  userId: string,
  store: RecentModulesStore | null = browserStore(),
): RecentModule[] {
  if (!store || !userId) return [];
  try {
    const raw = store.getItem(recentModulesKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { recent?: unknown } | null;
    if (!parsed || !Array.isArray(parsed.recent)) return [];
    const out: RecentModule[] = [];
    for (const entry of parsed.recent as unknown[]) {
      if (out.length >= RECENT_MODULES_LIMIT) break;
      if (!entry || typeof entry !== 'object') continue;
      const { id, title } = entry as { id?: unknown; title?: unknown };
      if (typeof id !== 'string' || !id || out.some((m) => m.id === id)) continue;
      out.push({ id, title: typeof title === 'string' ? title : '' });
    }
    return out;
  } catch {
    return [];
  }
}

function writeRecentModules(userId: string, list: RecentModule[], store: RecentModulesStore) {
  try {
    store.setItem(recentModulesKey(userId), JSON.stringify({ recent: list }));
  } catch {
    // хранилище недоступно или переполнено — модуль по умолчанию будет первым в списке
  }
}

/**
 * Карточки только что создали в этот модуль — он первым в недавних. Зовут после каждого
 * успешного создания: строка главной (словарь и вручную), `/cards/new`, `/units/:id/cards/new`,
 * `/cards/topics`. Выбор модуля в чипе без создания модуль по умолчанию не меняет.
 */
export function rememberComposerModule(
  userId: string,
  module: { id: string; title?: string | null },
  store: RecentModulesStore | null = browserStore(),
): RecentModule[] {
  if (!store || !userId || !module.id) return [];
  const previous = readRecentModules(userId, store);
  // Название ещё не загрузилось — оставляем прежнее, иначе чип показал бы пустоту.
  const title = module.title || previous.find((m) => m.id === module.id)?.title || '';
  const next = [{ id: module.id, title }, ...previous.filter((m) => m.id !== module.id)].slice(
    0,
    RECENT_MODULES_LIMIT,
  );
  writeRecentModules(userId, next, store);
  return next;
}

/** Модуль удалён (в другой вкладке, на другом устройстве) — убрать из недавних. */
export function forgetComposerModule(
  userId: string,
  moduleId: string,
  store: RecentModulesStore | null = browserStore(),
): RecentModule[] {
  if (!store || !userId) return [];
  const previous = readRecentModules(userId, store);
  const next = previous.filter((m) => m.id !== moduleId);
  if (next.length !== previous.length) writeRecentModules(userId, next, store);
  return next;
}

/**
 * Модуль по умолчанию: первый из недавних, который есть в свежем списке модулей; иначе первый
 * модуль списка (сервер отдаёт новые сверху — у новичка это стартовый); модулей нет — null.
 */
export function resolveDefaultModule<M extends { id: string }>(
  recent: readonly RecentModule[],
  modules: readonly M[],
): M | null {
  for (const entry of recent) {
    const found = modules.find((m) => m.id === entry.id);
    if (found) return found;
  }
  return modules[0] ?? null;
}
